// src/clientes/ClientesAdmin.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Global } from "../helpers/Global";

// ✅ Toast global
import { useToast } from "../hooks/useToast";

// ===== MUI =====
import {
  Box,
  Paper,
  Typography,
  Stack,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  FormControlLabel,
  Switch,
  Autocomplete,
  CircularProgress,
  Checkbox,
} from "@mui/material";

import { DataGrid } from "@mui/x-data-grid";

// Icons
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import RestoreIcon from "@mui/icons-material/Restore";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import GroupIcon from "@mui/icons-material/Group";
import SaveIcon from "@mui/icons-material/Save";

// ===== Leaflet =====
import {
  MapContainer,
  TileLayer,
  Marker,
  useMapEvents,
  LayersControl,
} from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import { GeoSearchControl, OpenStreetMapProvider } from "leaflet-geosearch";
import "leaflet-geosearch/dist/geosearch.css";

// Fix icon paths (leaflet)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

// =========================
// Helpers Leaflet
// =========================
const MapaClick = ({ setLatitud, setLongitud }) => {
  useMapEvents({
    click(e) {
      setLatitud(e.latlng.lat);
      setLongitud(e.latlng.lng);
    },
  });
  return null;
};

const CenterMapOnCoords = ({ lat, lng }) => {
  const map = useMapEvents({});
  useEffect(() => {
    if (lat != null && lng != null) map.setView([lat, lng]);
  }, [lat, lng, map]);
  return null;
};

const SearchControl = ({ setLatitud, setLongitud }) => {
  const map = useMapEvents({});
  useEffect(() => {
    const provider = new OpenStreetMapProvider();
    const search = new GeoSearchControl({
      provider,
      style: "bar",
      autoClose: true,
      showMarker: false,
      searchLabel: "Buscar ubicación...",
    });

    map.addControl(search);

    const handler = (result) => {
      const { x, y } = result.location;
      setLatitud(y);
      setLongitud(x);
    };

    map.on("geosearch/showlocation", handler);

    return () => {
      map.off("geosearch/showlocation", handler);
      map.removeControl(search);
    };
  }, [map, setLatitud, setLongitud]);

  return null;
};

export const ClientesAdmin = () => {
  const toast = useToast();

  const [loading, setLoading] = useState(true);

  const [clientes, setClientes] = useState([]);
  const [centros, setCentros] = useState([]);

  // Toggles
  const [mostrarClientesDesactivados, setMostrarClientesDesactivados] = useState(false);
  // ⬇️ se usa en el MODAL de centros (no en filtros globales)
  const [mostrarCentrosDesactivados, setMostrarCentrosDesactivados] = useState(false);

  // ===== Modal: Centros de un cliente =====
  const [modalCentros, setModalCentros] = useState(false);
  const [clienteCentros, setClienteCentros] = useState(null);

  // ===== Modal: Editar cliente =====
  const [modalEditarCliente, setModalEditarCliente] = useState(false);
  const [clienteEditando, setClienteEditando] = useState(null);
  const [editNombre, setEditNombre] = useState("");
  const [editCodigo, setEditCodigo] = useState("");
  const [savingCliente, setSavingCliente] = useState(false);

  // ===== Modal: Crear/Editar centro =====
  const [modalCentro, setModalCentro] = useState(false);
  const [savingCentro, setSavingCentro] = useState(false);
  const [modoEdicionCentro, setModoEdicionCentro] = useState(false);
  const [centroEditando, setCentroEditando] = useState(null);

  const [clienteIdCentro, setClienteIdCentro] = useState("");
  const [nuevoLugar, setNuevoLugar] = useState("");
  const [latitud, setLatitud] = useState(null);
  const [longitud, setLongitud] = useState(null);
  const [permiteJornadas, setPermiteJornadas] = useState(false);
  const [ubicacionInicial, setUbicacionInicial] = useState([40.4168, -3.7038]);
  const [errorCentro, setErrorCentro] = useState("");

  // ===== Modal: Responsables =====
  const [modalResp, setModalResp] = useState(false);
  const [centroResp, setCentroResp] = useState(null);
  const [responsables, setResponsables] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [respSeleccion, setRespSeleccion] = useState(null);
  const [respCargando, setRespCargando] = useState(false);
  const [respMsg, setRespMsg] = useState("");

  // =========================
  // Carga inicial
  // =========================
  useEffect(() => {
    navigator.geolocation.getCurrentPosition(
      (pos) => setUbicacionInicial([pos.coords.latitude, pos.coords.longitude]),
      () => {}
    );
  }, []);

  useEffect(() => {
    cargarTodo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cargarTodo = async () => {
    setLoading(true);
    try {
      const [resClientes, resCentros] = await Promise.all([
        fetch(`${Global.url}clientes/todos`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }),
        fetch(`${Global.url}centros/todos`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }),
      ]);

      if (!resClientes.ok) {
        toast.error("No se pudieron cargar los clientes.");
        setClientes([]);
      } else {
        const data = await resClientes.json().catch(() => []);
        setClientes(Array.isArray(data) ? data : []);
      }

      if (!resCentros.ok) {
        toast.error("No se pudieron cargar los centros.");
        setCentros([]);
      } else {
        const data = await resCentros.json().catch(() => []);
        setCentros(Array.isArray(data) ? data : []);
      }
    } catch (err) {
      console.error("Error cargando clientes/centros:", err);
      toast.error("Error de conexión al cargar clientes/centros.");
      setClientes([]);
      setCentros([]);
    } finally {
      setLoading(false);
    }
  };

  // =========================
  // Filtrado + rows
  // =========================
  const clientesFiltrados = useMemo(() => {
    return (clientes || []).filter((c) => (mostrarClientesDesactivados ? true : c.activo !== false));
  }, [clientes, mostrarClientesDesactivados]);

  const rowsClientes = useMemo(() => {
    return clientesFiltrados.map((c) => ({
      ...c,
      id: c.id,
      codigo_txt: c.codigo || "-",
      desactivado_txt: c.activo === false ? "Sí" : "",
    }));
  }, [clientesFiltrados]);

  const centrosDelCliente = useMemo(() => {
    const cid = clienteCentros?.id;
    if (!cid) return [];
    return (centros || [])
      .filter((ct) => Number(ct.cliente_id) === Number(cid))
      .filter((ct) => (mostrarCentrosDesactivados ? true : ct.activo !== false))
      .map((ct) => ({
        ...ct,
        id: ct.id,
        jornadas_txt: ct?.permite_jornadas ? "✔" : "✖",
        desactivado_txt: ct.activo === false ? "Sí" : "",
      }));
  }, [centros, clienteCentros, mostrarCentrosDesactivados]);

  // =========================
  // Acciones Cliente
  // =========================
  const abrirModalCentros = (cliente) => {
    setClienteCentros(cliente);
    setModalCentros(true);
  };

  const cerrarModalCentros = () => {
    setModalCentros(false);
    setClienteCentros(null);
  };

  const abrirEditarCliente = (cliente) => {
    setClienteEditando(cliente);
    setEditNombre(cliente?.nombre || "");
    setEditCodigo(cliente?.codigo || "");
    setModalEditarCliente(true);
  };

  const cerrarEditarCliente = () => {
    if (savingCliente) return;
    setModalEditarCliente(false);
    setClienteEditando(null);
    setEditNombre("");
    setEditCodigo("");
  };

  const guardarCambiosCliente = async () => {
    if (!clienteEditando?.id) return;
    if (!editNombre.trim()) {
      toast.warning("El nombre del cliente es obligatorio.");
      return;
    }

    setSavingCliente(true);
    try {
      const res = await fetch(`${Global.url}clientes/${clienteEditando.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({ nombre: editNombre.trim(), codigo: (editCodigo || "").trim() }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast.error(data?.message || "No se pudo guardar el cliente.");
        return;
      }

      toast.success("Cliente actualizado.");
      cerrarEditarCliente();
      await cargarTodo();
    } catch (err) {
      console.error("Error al guardar cliente:", err);
      toast.error("Error de conexión al guardar el cliente.");
    } finally {
      setSavingCliente(false);
    }
  };

  const eliminarCliente = async (id) => {
    if (!window.confirm("¿Eliminar este cliente?")) return;

    try {
      const res = await fetch(`${Global.url}clientes/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });

      if (!res.ok) {
        const t = await res.text().catch(() => "");
        toast.error(t || "No se pudo eliminar el cliente.");
        return;
      }

      toast.success("Cliente eliminado.");
      await cargarTodo();
    } catch (err) {
      console.error("Error al eliminar cliente:", err);
      toast.error("Error de conexión al eliminar el cliente.");
    }
  };

  const reactivarCliente = async (id) => {
    if (!window.confirm("¿Reactivar este cliente?")) return;

    try {
      const res = await fetch(`${Global.url}clientes/${id}/reactivar`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast.error(data?.message || "No se pudo reactivar el cliente.");
        return;
      }

      toast.success("Cliente reactivado.");
      await cargarTodo();
    } catch (err) {
      console.error("Error al reactivar cliente:", err);
      toast.error("Error de conexión al reactivar el cliente.");
    }
  };

  // =========================
  // Acciones Centro
  // =========================
  const abrirCrearCentro = (cliente) => {
    setClienteIdCentro(cliente?.id ? String(cliente.id) : "");
    setModoEdicionCentro(false);
    setCentroEditando(null);

    setNuevoLugar("");
    setLatitud(null);
    setLongitud(null);
    setPermiteJornadas(false);
    setErrorCentro("");

    setModalCentro(true);
  };

  const abrirEditarCentro = (centro) => {
    setClienteIdCentro(centro?.cliente_id ? String(centro.cliente_id) : "");
    setModoEdicionCentro(true);
    setCentroEditando(centro);

    setNuevoLugar(centro?.nombre || "");
    const lat = centro?.latitud != null ? Number(centro.latitud) : null;
    const lng = centro?.longitud != null ? Number(centro.longitud) : null;

    setLatitud(Number.isFinite(lat) ? lat : null);
    setLongitud(Number.isFinite(lng) ? lng : null);
    setPermiteJornadas(!!centro?.permite_jornadas);
    setErrorCentro("");

    if (Number.isFinite(lat) && Number.isFinite(lng)) {
      setUbicacionInicial([lat, lng]);
    }

    setModalCentro(true);
  };

  const cerrarModalCentro = () => {
    if (savingCentro) return;

    setModalCentro(false);
    setModoEdicionCentro(false);
    setCentroEditando(null);
    setClienteIdCentro("");

    setNuevoLugar("");
    setLatitud(null);
    setLongitud(null);
    setPermiteJornadas(false);
    setErrorCentro("");
  };

  const guardarCentro = async () => {
    if (!clienteIdCentro) {
      setErrorCentro("Selecciona un cliente válido.");
      return;
    }
    if (!nuevoLugar.trim()) {
      setErrorCentro("El nombre del centro es obligatorio.");
      return;
    }
    if (latitud == null || longitud == null || Number.isNaN(latitud) || Number.isNaN(longitud)) {
      setErrorCentro("Selecciona una ubicación válida en el mapa.");
      return;
    }

    setSavingCentro(true);

    const payload = {
      cliente_id: Number(clienteIdCentro),
      nombre: nuevoLugar.trim(),
      latitud,
      longitud,
      permite_jornadas: !!permiteJornadas,
    };

    const url = modoEdicionCentro
      ? `${Global.url}centros/${centroEditando?.id}`
      : `${Global.url}centros`;

    const method = modoEdicionCentro ? "PUT" : "POST";

    try {
      const res = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setErrorCentro(data?.message || "No se pudo guardar el centro.");
        return;
      }

      toast.success(modoEdicionCentro ? "Centro actualizado." : "Centro creado.");
      cerrarModalCentro();
      await cargarTodo();
    } catch (err) {
      console.error("Error al guardar centro:", err);
      setErrorCentro("Error de conexión al guardar el centro.");
    } finally {
      setSavingCentro(false);
    }
  };

  const eliminarCentro = async (id) => {
    if (!window.confirm("¿Eliminar este centro?")) return;

    try {
      const res = await fetch(`${Global.url}centros/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });

      if (!res.ok) {
        const t = await res.text().catch(() => "");
        toast.error(t || "No se pudo eliminar el centro.");
        return;
      }

      toast.success("Centro eliminado.");
      await cargarTodo();
    } catch (err) {
      console.error("Error al eliminar centro:", err);
      toast.error("Error de conexión al eliminar el centro.");
    }
  };

  const reactivarCentro = async (id) => {
    if (!window.confirm("¿Reactivar este centro?")) return;

    try {
      const res = await fetch(`${Global.url}centros/${id}/reactivar`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });

      if (!res.ok) {
        const t = await res.text().catch(() => "");
        toast.error(t || "No se pudo reactivar el centro.");
        return;
      }

      toast.success("Centro reactivado.");
      await cargarTodo();
    } catch (err) {
      console.error("Error al reactivar centro:", err);
      toast.error("Error de conexión al reactivar el centro.");
    }
  };

  // =========================
  // Responsables
  // =========================
  const abrirModalResponsables = async (centro) => {
    setCentroResp(centro);
    setResponsables([]);
    setUsuarios([]);
    setRespSeleccion(null);
    setRespMsg("");
    setModalResp(true);
    await cargarResponsablesYUsuarios(centro?.id);
  };

  const cerrarModalResponsables = () => {
    if (respCargando) return;
    setModalResp(false);
    setCentroResp(null);
    setResponsables([]);
    setUsuarios([]);
    setRespSeleccion(null);
    setRespMsg("");
  };

  const cargarResponsablesYUsuarios = async (centroId) => {
    if (!centroId) return;
    setRespCargando(true);
    setRespMsg("");

    try {
      const [r1, r2] = await Promise.all([
        fetch(`${Global.url}centros/${centroId}/responsables`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }),
        fetch(`${Global.url}usuarios/todos`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }),
      ]);

      const resp = await r1.json().catch(() => []);
      const usrs = await r2.json().catch(() => []);

      setResponsables(Array.isArray(resp) ? resp : []);
      setUsuarios(Array.isArray(usrs) ? usrs.filter((u) => u.activo !== false) : []);
    } catch (e) {
      console.error(e);
      setRespMsg("No se pudieron cargar los datos.");
    } finally {
      setRespCargando(false);
    }
  };

  const usuarioOptionsResp = useMemo(() => {
    const idsAsignados = new Set((responsables || []).map((r) => Number(r.id)));
    return (usuarios || [])
      .filter((u) => !idsAsignados.has(Number(u.id)))
      .slice()
      .sort((a, b) =>
        `${a.nombre} ${a.apellidos || ""}`.localeCompare(`${b.nombre} ${b.apellidos || ""}`)
      );
  }, [usuarios, responsables]);

  const quitarResponsable = async (usuarioId) => {
    if (!centroResp?.id) return;
    setRespCargando(true);
    setRespMsg("");

    try {
      const res = await fetch(`${Global.url}centros/${centroResp.id}/responsables/${usuarioId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });

      if (!res.ok) {
        const t = await res.text().catch(() => "");
        setRespMsg(t || "No se pudo quitar el usuario.");
        return;
      }

      await cargarResponsablesYUsuarios(centroResp.id);
    } catch (e) {
      console.error(e);
      setRespMsg("Error de conexión al quitar el usuario.");
    } finally {
      setRespCargando(false);
    }
  };

  const agregarResponsable = async () => {
    if (!centroResp?.id || !respSeleccion?.id) return;

    setRespCargando(true);
    setRespMsg("");

    try {
      const res = await fetch(`${Global.url}centros/${centroResp.id}/responsables`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({ usuarios_id: [Number(respSeleccion.id)] }),
      });

      if (!res.ok) {
        const t = await res.text().catch(() => "");
        setRespMsg(t || "No se pudo agregar el usuario.");
        return;
      }

      await cargarResponsablesYUsuarios(centroResp.id);
      setRespSeleccion(null);
    } catch (e) {
      console.error(e);
      setRespMsg("Error de conexión al agregar el usuario.");
    } finally {
      setRespCargando(false);
    }
  };

  // =========================
  // DataGrid columns
  // =========================
  const columnsClientes = useMemo(() => {
    const base = [

      // ✅ hacemos “Cliente” menos greedy: minWidth + flex pequeño
      { field: "nombre", headerName: "Cliente", minWidth: 240, flex: 0.6 },

      { field: "codigo_txt", headerName: "Código", width: 160 },
    ];

    if (mostrarClientesDesactivados) {
      base.push({
        field: "desactivado_txt",
        headerName: "Desactivado",
        width: 140,
        sortable: false,
      });
    }

    // ✅ Acciones más ancha (para que los botones tengan espacio)
    base.push({
      field: "acciones",
      headerName: "Acciones",
      sortable: false,
      width: 520, // 👈 clave (ajusta 480–600)
      renderCell: (params) => {
        const c = params.row;
        const eliminado = c.activo === false;

        return (
          <Box sx={{ display: "flex", gap: 1, alignItems: "center", height: "100%", width: "100%" }}>
            <Button size="small" startIcon={<LocationOnIcon />} onClick={() => abrirModalCentros(c)}>
              Centros
            </Button>

            <Button size="small" startIcon={<AddIcon />} onClick={() => abrirCrearCentro(c)}>
              Añadir centro
            </Button>

            <Button size="small" startIcon={<EditIcon />} onClick={() => abrirEditarCliente(c)}>
              Editar
            </Button>

            {eliminado ? (
              <Button size="small" startIcon={<RestoreIcon />} onClick={() => reactivarCliente(c.id)}>
                Reactivar
              </Button>
            ) : (
              <Button size="small" startIcon={<DeleteIcon />} onClick={() => eliminarCliente(c.id)}>
                Eliminar
              </Button>
            )}
          </Box>
        );
      },
    });

    return base;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mostrarClientesDesactivados, clientes, centros]);

  const columnsCentros = useMemo(() => {
    const base = [
      { field: "id", headerName: "ID", width: 90 },
      { field: "nombre", headerName: "Centro", flex: 1, minWidth: 260 },
      { field: "jornadas_txt", headerName: "Jornadas", width: 120, sortable: false },
    ];

    if (mostrarCentrosDesactivados) {
      base.push({
        field: "desactivado_txt",
        headerName: "Desactivado",
        width: 140,
        sortable: false,
      });
    }

    base.push({
      field: "acciones",
      headerName: "Acciones",
      sortable: false,
      width: 420,
      renderCell: (params) => {
        const ct = params.row;
        const eliminado = ct.activo === false;

        return (
          <Box sx={{ display: "flex", gap: 1, alignItems: "center", height: "100%", width: "100%" }}>
            <Button size="small" startIcon={<EditIcon />} onClick={() => abrirEditarCentro(ct)}>
              Editar
            </Button>

            <Button size="small" startIcon={<GroupIcon />} onClick={() => abrirModalResponsables(ct)}>
              Responsables
            </Button>

            {eliminado ? (
              <Button size="small" startIcon={<RestoreIcon />} onClick={() => reactivarCentro(ct.id)}>
                Reactivar
              </Button>
            ) : (
              <Button size="small" startIcon={<DeleteIcon />} onClick={() => eliminarCentro(ct.id)}>
                Eliminar
              </Button>
            )}
          </Box>
        );
      },
    });

    return base;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mostrarCentrosDesactivados, clientes, centros]);

  const columnsResp = useMemo(
    () => [
      { field: "id", headerName: "ID", width: 90 },
      { field: "nombre", headerName: "Nombre", flex: 1, minWidth: 180 },
      {
        field: "apellidos",
        headerName: "Apellidos",
        flex: 1,
        minWidth: 200,
        valueGetter: (p) => p?.row?.apellidos || "-",
      },
      {
        field: "email",
        headerName: "Email",
        flex: 1,
        minWidth: 220,
        valueGetter: (p) => p?.row?.email || "-",
      },
      {
        field: "acciones",
        headerName: "Acciones",
        sortable: false,
        width: 160,
        renderCell: (params) => (
          <Button size="small" startIcon={<DeleteIcon />} onClick={() => quitarResponsable(params.row.id)}>
            Quitar
          </Button>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [centroResp, responsables]
  );

  // =========================
  // Render
  // =========================
  return (
    <Box className="appContainer appContainer--wide">
      <Stack spacing={2}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          Gestión de Clientes y Centros
        </Typography>

        {/* ✅ Filtros (solo clientes desactivados) */}
        <Paper sx={{ p: 2 }}>
          <Stack
            spacing={2}
            direction={{ xs: "column", md: "row" }}
            sx={{ alignItems: { md: "center" } }}
          >
            <FormControlLabel
              control={
                <Switch
                  checked={mostrarClientesDesactivados}
                  onChange={(e) => setMostrarClientesDesactivados(e.target.checked)}
                />
              }
              label="Mostrar clientes desactivados"
            />
          </Stack>
        </Paper>

        {/* Tabla Clientes */}
        <Paper sx={{ height: 640, p: 1 }}>
          <DataGrid
            rows={rowsClientes}
            columns={columnsClientes}
            getRowId={(row) => row.id}
            disableRowSelectionOnClick
            loading={loading}
            pagination
            initialState={{ pagination: { paginationModel: { page: 0, pageSize: 25 } } }}
            pageSizeOptions={[10, 25, 50, 100]}
            getRowClassName={(params) => (params.row.activo === false ? "row-disabled" : "")}
            slots={{
              noRowsOverlay: () => (
                <Box sx={{ p: 2, color: "text.secondary" }}>
                  {loading ? "Cargando…" : "No hay clientes que mostrar con estos filtros."}
                </Box>
              ),
            }}
            sx={{
              "& .MuiDataGrid-columnHeaders": { borderBottom: "1px solid rgba(0,0,0,0.08)" },
              "& .MuiDataGrid-cell": { borderBottom: "1px solid rgba(0,0,0,0.06)" },
              "& .row-disabled": { opacity: 0.55 },
            }}
          />
        </Paper>

        {/* =========================
            Dialog: Centros del cliente (DataGrid)
           ========================= */}
        <Dialog open={modalCentros} onClose={cerrarModalCentros} fullWidth maxWidth="lg">
          <DialogTitle>
            Centros de {clienteCentros?.nombre ? `“${clienteCentros.nombre}”` : "cliente"}
          </DialogTitle>
          <Divider />
          <DialogContent sx={{ pt: 2 }}>
            <Stack spacing={2}>
              {/* ✅ Botón + toggle en la misma “línea” (sin Paper de filtros) */}
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                sx={{ alignItems: { sm: "center" }, justifyContent: "space-between" }}
              >
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ alignItems: { sm: "center" } }}>
                  <Button
                    startIcon={<AddIcon />}
                    onClick={() => abrirCrearCentro(clienteCentros)}
                    disabled={!clienteCentros?.id}
                  >
                    Añadir centro
                  </Button>

                  <FormControlLabel
                    control={
                      <Switch
                        checked={mostrarCentrosDesactivados}
                        onChange={(e) => setMostrarCentrosDesactivados(e.target.checked)}
                      />
                    }
                    label="Mostrar centros desactivados"
                  />
                </Stack>

                <Typography sx={{ color: "text.secondary" }}>
                  {centrosDelCliente.length} centro{centrosDelCliente.length !== 1 ? "s" : ""}.
                </Typography>
              </Stack>

              <Paper sx={{ height: 520, p: 1 }}>
                <DataGrid
                  rows={centrosDelCliente}
                  columns={columnsCentros}
                  getRowId={(row) => row.id}
                  disableRowSelectionOnClick
                  loading={loading}
                  pagination
                  initialState={{ pagination: { paginationModel: { page: 0, pageSize: 25 } } }}
                  pageSizeOptions={[10, 25, 50, 100]}
                  getRowClassName={(params) => (params.row.activo === false ? "row-disabled" : "")}
                  sx={{
                    "& .MuiDataGrid-columnHeaders": { borderBottom: "1px solid rgba(0,0,0,0.08)" },
                    "& .MuiDataGrid-cell": { borderBottom: "1px solid rgba(0,0,0,0.06)" },
                    "& .row-disabled": { opacity: 0.55 },
                  }}
                />
              </Paper>
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={cerrarModalCentros}>
              Cerrar
            </Button>
          </DialogActions>
        </Dialog>

        {/* =========================
            Dialog: Editar cliente
           ========================= */}
        <Dialog open={modalEditarCliente} onClose={cerrarEditarCliente} fullWidth maxWidth="sm">
          <DialogTitle>Editar cliente</DialogTitle>
          <Divider />
          <DialogContent sx={{ pt: 2 }}>
            <Stack spacing={2}>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                {clienteEditando ? `Cliente #${clienteEditando.id}` : ""}
              </Typography>

              <TextField
                label="Nombre"
                value={editNombre}
                onChange={(e) => setEditNombre(e.target.value)}
                required
              />

              <TextField
                label="Código"
                value={editCodigo}
                onChange={(e) => setEditCodigo(e.target.value)}
                placeholder="Opcional"
              />
            </Stack>
          </DialogContent>

          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={cerrarEditarCliente} disabled={savingCliente}>
              Cancelar
            </Button>
            <Button onClick={guardarCambiosCliente} disabled={savingCliente} startIcon={<SaveIcon />}>
              {savingCliente ? "Guardando..." : "Guardar"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* =========================
            Dialog: Crear / Editar centro (mapa)
           ========================= */}
        <Dialog open={modalCentro} onClose={cerrarModalCentro} fullWidth maxWidth="md">
          <DialogTitle>{modoEdicionCentro ? "Editar centro" : "Añadir centro"}</DialogTitle>
          <Divider />
          <DialogContent sx={{ pt: 2 }}>
            <Stack spacing={2}>
              <Autocomplete
                size="small"
                options={(clientes || []).slice().sort((a, b) => (a.nombre || "").localeCompare(b.nombre || ""))}
                getOptionLabel={(c) => c?.nombre || ""}
                isOptionEqualToValue={(opt, val) => String(opt.id) === String(val.id)}
                value={(clientes || []).find((c) => String(c.id) === String(clienteIdCentro)) || null}
                onChange={(_, v) => setClienteIdCentro(v ? String(v.id) : "")}
                loading={loading}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Cliente"
                    placeholder="Selecciona un cliente"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loading ? <CircularProgress size={18} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                clearOnEscape
              />

              <TextField
                label="Nombre del centro"
                value={nuevoLugar}
                onChange={(e) => setNuevoLugar(e.target.value)}
                required
              />

              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                Selecciona ubicación en el mapa o introduce coordenadas.
              </Typography>

              <Box sx={{ width: "100%", borderRadius: 2, overflow: "hidden" }}>
                <MapContainer center={ubicacionInicial} zoom={15} style={{ height: 360, width: "100%" }}>
                  <LayersControl position="topright">
                    <LayersControl.BaseLayer checked name="Satélite + Callejero">
                      <TileLayer
                        url={`https://api.maptiler.com/maps/hybrid/256/{z}/{x}/{y}.jpg?key=${process.env.REACT_APP_MAPTILER_KEY}`}
                        attribution="&copy; MapTiler"
                      />
                    </LayersControl.BaseLayer>

                    <LayersControl.BaseLayer name="Callejero">
                      <TileLayer
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        attribution="&copy; OpenStreetMap contributors"
                      />
                    </LayersControl.BaseLayer>
                  </LayersControl>

                  <MapaClick setLatitud={setLatitud} setLongitud={setLongitud} />
                  <SearchControl setLatitud={setLatitud} setLongitud={setLongitud} />
                  <CenterMapOnCoords lat={latitud} lng={longitud} />

                  {latitud != null && longitud != null && (
                    <Marker
                      position={[latitud, longitud]}
                      eventHandlers={{
                        click: () => {
                          setLatitud(null);
                          setLongitud(null);
                        },
                      }}
                    />
                  )}
                </MapContainer>
              </Box>

              <TextField
                label="Coordenadas (lat, lng)"
                placeholder="Ej: 40.4168, -3.7038"
                value={latitud != null && longitud != null ? `${latitud}, ${longitud}` : ""}
                onChange={(e) => {
                  const [lat, lng] = e.target.value.split(",").map((n) => Number.parseFloat(n.trim()));
                  if (!Number.isNaN(lat) && !Number.isNaN(lng)) {
                    setLatitud(lat);
                    setLongitud(lng);
                  }
                }}
              />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={permiteJornadas}
                    onChange={(e) => setPermiteJornadas(e.target.checked)}
                  />
                }
                label="Este centro permite registrar jornadas"
              />

              {errorCentro && (
                <Typography color="error" sx={{ fontWeight: 600 }}>
                  {errorCentro}
                </Typography>
              )}
            </Stack>
          </DialogContent>

          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={cerrarModalCentro} disabled={savingCentro}>
              Cancelar
            </Button>
            <Button onClick={guardarCentro} disabled={savingCentro} startIcon={<SaveIcon />}>
              {savingCentro ? "Guardando..." : "Guardar"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* =========================
            Dialog: Responsables
           ========================= */}
        <Dialog open={modalResp} onClose={cerrarModalResponsables} fullWidth maxWidth="md">
          <DialogTitle>Responsables de “{centroResp?.nombre || ""}”</DialogTitle>
          <Divider />
          <DialogContent sx={{ pt: 2 }}>
            <Stack spacing={2}>
              {respMsg && (
                <Typography color="error" sx={{ fontWeight: 600 }}>
                  {respMsg}
                </Typography>
              )}

              <Stack spacing={2} direction={{ xs: "column", md: "row" }} sx={{ alignItems: { md: "center" } }}>
                <Autocomplete
                  size="small"
                  options={usuarioOptionsResp}
                  getOptionLabel={(u) =>
                    `${u.nombre} ${u.apellidos || ""} (${u.email || u.dni || "s/d"})`.trim()
                  }
                  isOptionEqualToValue={(opt, val) => String(opt.id) === String(val.id)}
                  value={respSeleccion}
                  onChange={(_, v) => setRespSeleccion(v)}
                  loading={respCargando}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Añadir responsable"
                      placeholder="Buscar usuario…"
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {respCargando ? <CircularProgress size={18} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  sx={{ minWidth: 280, flex: 1 }}
                  clearOnEscape
                />

                <Button
                  startIcon={<AddIcon />}
                  onClick={agregarResponsable}
                  disabled={respCargando || !respSeleccion}
                >
                  Añadir
                </Button>
              </Stack>

              <Paper sx={{ height: 420, p: 1 }}>
                <DataGrid
                  rows={(responsables || []).map((u) => ({ ...u, id: u.id }))}
                  columns={columnsResp}
                  getRowId={(row) => row.id}
                  disableRowSelectionOnClick
                  loading={respCargando}
                  pagination
                  initialState={{ pagination: { paginationModel: { page: 0, pageSize: 25 } } }}
                  pageSizeOptions={[10, 25, 50]}
                  sx={{
                    "& .MuiDataGrid-columnHeaders": { borderBottom: "1px solid rgba(0,0,0,0.08)" },
                    "& .MuiDataGrid-cell": { borderBottom: "1px solid rgba(0,0,0,0.06)" },
                  }}
                />
              </Paper>

              <Typography sx={{ textAlign: "right", color: "text.secondary" }}>
                {responsables.length} responsable{responsables.length !== 1 ? "s" : ""} asignado
                {responsables.length !== 1 ? "s" : ""}.
              </Typography>
            </Stack>
          </DialogContent>

          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={cerrarModalResponsables} disabled={respCargando}>
              Cerrar
            </Button>
          </DialogActions>
        </Dialog>
      </Stack>
    </Box>
  );
};
